import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPalette,
  faBoxArchive,
  faInbox,
  faEnvelopeOpen,
  faEnvelope,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  useGetInboxQuery,
  useGetInboxCountsQuery,
  useArchiveConversationMutation,
  useBulkActionMutation,
  useGetUnreadCountQuery,
} from 'store/services/message/messageApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useRowSelection } from 'hooks/useRowSelection';
import { useIsElite } from 'hooks/useIsElite';
import { useIsMobile } from 'hooks/useIsMobile';
import { RootState } from 'store';
import { AssetUrls } from 'config/assets';
import BubbleColorPickerModal from 'components/BubbleColorPickerModal/BubbleColorPickerModal';
import ContextMenu, { ContextMenuItem } from 'components/ContextMenu/ContextMenu';
import MagicButton from 'components/MagicButton/MagicButton';
import type { ThreadTypeFilter } from 'types/interfaces/thread';
import InboxStatusTabs, { InboxTab } from './components/InboxStatusTabs';
import { BulkAction } from './components/InboxActionBar';
import TypeFilterPills from './components/TypeFilterPills';
import ThreadList from './components/ThreadList';
import ThreadPane from './components/ThreadPane';
import ContextRail from './components/ContextRail';
import MobileContextStrip from './components/MobileContextStrip';
import ContextSheet from './components/ContextSheet';
import 'styles/caseStatusBadges.css';
import './InboxPage.css';

export const INBOX_PAGE_SIZE = 25;

/**
 * While the workspace owns the viewport (desktop always, mobile with a thread
 * open) this class on body bounds the app shell to 100dvh, hides the footer and
 * makes the panes the only scroll regions. Same mechanism as the offer
 * builder's BUILDER_SCROLL_BODY_CLASS.
 */
export const INBOX_FIXED_BODY_CLASS = 'inbox-fixed-page';

/** Reads --nav-transition-duration so the close animation and the unmount agree. */
const navTransitionMs = (): number => {
  if (typeof window === 'undefined') return 0;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--nav-transition-duration').trim();
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return 0;
  return raw.endsWith('ms') ? n : n * 1000;
};

/**
 * The inbox workspace (Asana 1219952262403623): the thread list on the left,
 * the open thread in the middle, the subject's context rail on the right.
 * Routes stay /inbox and /inbox/:conversationId; both render this page.
 * ≤768px shows one pane at a time.
 */
export default function InboxPage() {
  const navigate = useNavigate();
  const { conversationId: activeId } = useParams<{ conversationId: string }>();
  const isMobile = useIsMobile();

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<InboxTab>('inbox');
  const [typeFilter, setTypeFilter] = useState<ThreadTypeFilter>('all');
  const [contextMenu, setContextMenu] = useState<{ threadId: string; x: number; y: number } | null>(null);
  const [showBubbleColorModal, setShowBubbleColorModal] = useState(false);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  // The thread kept mounted while its pane slides shut; equals activeId
  // whenever a thread is selected.
  const [renderedId, setRenderedId] = useState<string | undefined>(activeId);
  const workspaceRef = useRef<HTMLDivElement>(null);

  const { data: meData } = useGetMeQuery();
  const { isElite } = useIsElite();
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);
  const isSocketConnected = useSelector((state: RootState) => state.message.isConnected);

  // Live updates arrive over the socket as list invalidations; poll only as a
  // fallback while disconnected, and never while rate limited or in maintenance.
  const pollingInterval = (isRateLimited || isMaintenanceMode) ? 0 : isSocketConnected ? 0 : 60000;

  const archived = activeTab === 'archived';
  const { data, isLoading, error } = useGetInboxQuery(
    {
      page,
      pageSize: INBOX_PAGE_SIZE,
      search: debouncedSearch || undefined,
      archived,
      type: typeFilter,
    },
    { pollingInterval, refetchOnMountOrArgChange: true }
  );

  // Archived tab count: total of archived threads (lightweight pageSize=1 query)
  const { data: archivedCountData } = useGetInboxQuery(
    { page: 1, pageSize: 1, archived: true },
    { refetchOnMountOrArgChange: true }
  );
  const archivedCount = archivedCountData?.pagination?.total ?? 0;

  const { data: unreadData } = useGetUnreadCountQuery();
  const unreadCount = unreadData?.count ?? 0;
  const { data: inboxCounts } = useGetInboxCountsQuery(undefined, { pollingInterval });

  const [archiveConversation] = useArchiveConversationMutation();
  const [bulkAction, { isLoading: isBulkLoading }] = useBulkActionMutation();

  const rows = useMemo(() => data?.conversations ?? [], [data?.conversations]);
  const visibleIds = useMemo(() => rows.map((c) => c.conversationId), [rows]);
  const {
    selectedIds,
    allSelected: allVisibleSelected,
    handleClick: handleSelectionClick,
    toggleAll: toggleSelectAll,
    clear: clearSelection,
  } = useRowSelection<string>(visibleIds);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter]);

  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [activeTab, clearSelection]);

  const openThread = useCallback(
    (conversationId: string) => navigate(`/inbox/${conversationId}`),
    [navigate]
  );

  // The details sheet belongs to one thread; leaving it closes the sheet.
  useEffect(() => {
    setShowDetailsSheet(false);
  }, [activeId]);

  // Opening renders the thread at once so it slides in with the pane; closing
  // keeps it mounted for the width transition, then unmounts it.
  useEffect(() => {
    if (activeId) {
      setRenderedId(activeId);
      return undefined;
    }
    const timer = setTimeout(() => setRenderedId(undefined), navTransitionMs() + 50);
    return () => clearTimeout(timer);
  }, [activeId]);

  // Desktop always, mobile only with a thread open: the shell stops scrolling
  // and the footer goes, so the composer sits at the bottom of the viewport.
  const fixedLayout = !isMobile || Boolean(activeId);
  useEffect(() => {
    if (!fixedLayout) return undefined;
    document.body.classList.add(INBOX_FIXED_BODY_CLASS);
    return () => document.body.classList.remove(INBOX_FIXED_BODY_CLASS);
  }, [fixedLayout]);

  // The open conversation pane and the content inside it share one width,
  // measured from the workspace, so the content is clipped rather than
  // reflowed while the pane animates (--inbox-ws-width in InboxPage.css).
  useLayoutEffect(() => {
    const el = workspaceRef.current;
    if (!el) return undefined;
    const publish = () => {
      const cs = getComputedStyle(el);
      const content = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      el.style.setProperty('--inbox-ws-width', `${Math.max(0, Math.round(content))}px`);
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Escape deselects the open thread on desktop. A first Escape inside a
  // field with text only leaves the field; open dialogs keep their own Escape.
  useEffect(() => {
    if (!activeId || isMobile) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return;
      const target = e.target as HTMLElement | null;
      if (target && (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) && target.value) {
        target.blur();
        return;
      }
      navigate('/inbox');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeId, isMobile, navigate]);

  const handleArchive = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await archiveConversation(conversationId).unwrap();
      toast.success('Conversation archived');
      if (conversationId === activeId) navigate('/inbox');
    } catch {
      toast.error('Failed to archive conversation');
    }
  };

  const handleCheckboxClick = (conversationId: string, e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    // Plain click and Ctrl/Cmd+Click both toggle a single row; Shift adds the range.
    handleSelectionClick(conversationId, { shiftKey: e.shiftKey });
  };

  const performBulkAction = async (
    threadIds: string[],
    action: BulkAction,
    opts: { clearSelectionOnSuccess?: boolean } = {}
  ) => {
    if (threadIds.length === 0) return;

    if (action === 'report_spam') {
      const confirmed = window.confirm(
        `Report ${threadIds.length} conversation${threadIds.length === 1 ? '' : 's'} as spam? ` +
        'The thread will be archived and our team will be notified.'
      );
      if (!confirmed) return;
    }

    try {
      const result = await bulkAction({ threadIds, action }).unwrap();
      const n = result.affected ?? threadIds.length;
      const messages: Record<BulkAction, string> = {
        archive: `Archived ${n} conversation${n === 1 ? '' : 's'}`,
        unarchive: `Moved ${n} conversation${n === 1 ? '' : 's'} to Inbox`,
        mark_read: `Marked ${n} conversation${n === 1 ? '' : 's'} as read`,
        mark_unread: `Marked ${n} conversation${n === 1 ? '' : 's'} as unread`,
        report_spam: `Reported ${n} conversation${n === 1 ? '' : 's'} as spam`,
      };
      toast.success(messages[action]);
      if (opts.clearSelectionOnSuccess) {
        // Keeps the shift anchor, as this page always has after a bulk action.
        clearSelection({ keepAnchor: true });
      }
    } catch {
      toast.error('Bulk action failed. Please try again.');
    }
  };

  const handleBulkAction = (action: BulkAction) =>
    performBulkAction(Array.from(selectedIds), action, { clearSelectionOnSuccess: true });

  const handleRowContextMenu = (threadId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ threadId, x: e.clientX, y: e.clientY });
  };

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const contextMenuItems: ContextMenuItem[] = useMemo(() => {
    if (!contextMenu) return [];
    const { threadId } = contextMenu;
    const isMultiSelect = selectedIds.has(threadId) && selectedIds.size > 1;
    const targetIds = isMultiSelect ? Array.from(selectedIds) : [threadId];
    const run = (action: BulkAction) =>
      performBulkAction(targetIds, action, { clearSelectionOnSuccess: isMultiSelect });
    return [
      archived
        ? { key: 'unarchive', label: 'Move to Inbox', onSelect: () => run('unarchive') }
        : { key: 'archive', label: 'Archive', onSelect: () => run('archive') },
      { key: 'mark_read', label: 'Mark as Read', onSelect: () => run('mark_read') },
      { key: 'mark_unread', label: 'Mark as Unread', onSelect: () => run('mark_unread') },
      { key: 'report_spam', label: 'Report Spam', danger: true, onSelect: () => run('report_spam') },
    ];
    // performBulkAction closes over the latest mutation hooks; the menu is short-lived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, selectedIds, archived]);

  const contextMenuSubtitle =
    contextMenu && selectedIds.has(contextMenu.threadId) && selectedIds.size > 1
      ? `${selectedIds.size} threads selected`
      : undefined;

  const handleEliteRedirect = () => navigate('/elite?source=bubble-colors');

  const listPane = (
    <section className="inbox-list-pane" aria-label="Threads">
      <div className="inbox-list-header">
        <h1>Inbox</h1>
        <button
          className="inbox-customize-button"
          onClick={() => setShowBubbleColorModal(true)}
          title="Customize your chat style"
          type="button"
        >
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="palette-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6F51E4" />
                <stop offset="100%" stopColor="#E02CDC" />
              </linearGradient>
            </defs>
          </svg>
          <FontAwesomeIcon icon={faPalette} />
        </button>
      </div>

      <InboxStatusTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        unreadCount={unreadCount}
        archivedCount={archivedCount}
      />

      <div className="inbox-search-container">
        <input
          type="text"
          placeholder="Search user, card, trade # or case #"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="inbox-search"
          aria-label="Search threads"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="clear-search-button" type="button" aria-label="Clear search">
            ×
          </button>
        )}
      </div>

      <TypeFilterPills value={typeFilter} onChange={setTypeFilter} counts={inboxCounts} />

      {!isMobile && rows.length > 0 && (
        <div className="inbox-list-toolbar">
          <label className="inbox-select-all">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              aria-label="Select all"
            />
            <span>{selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}</span>
          </label>
          {selectedIds.size > 0 && (
            <div className="inbox-inline-actions">
              {archived ? (
                <button type="button" className="inbox-inline-action-button" disabled={isBulkLoading} onClick={() => handleBulkAction('unarchive')} title="Move to Inbox">
                  <FontAwesomeIcon icon={faInbox} />
                </button>
              ) : (
                <button type="button" className="inbox-inline-action-button" disabled={isBulkLoading} onClick={() => handleBulkAction('archive')} title="Archive">
                  <FontAwesomeIcon icon={faBoxArchive} />
                </button>
              )}
              <button type="button" className="inbox-inline-action-button" disabled={isBulkLoading} onClick={() => handleBulkAction('mark_read')} title="Mark as Read">
                <FontAwesomeIcon icon={faEnvelopeOpen} />
              </button>
              <button type="button" className="inbox-inline-action-button" disabled={isBulkLoading} onClick={() => handleBulkAction('mark_unread')} title="Mark as Unread">
                <FontAwesomeIcon icon={faEnvelope} />
              </button>
              <button type="button" className="inbox-inline-action-button inbox-inline-action-button--danger" disabled={isBulkLoading} onClick={() => handleBulkAction('report_spam')} title="Report Spam">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </button>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="inbox-loading">Loading messages...</div>
      ) : error ? (
        <div className="inbox-error">Failed to load messages. Please try again.</div>
      ) : rows.length === 0 ? (
        <div className="inbox-empty">
          {debouncedSearch || typeFilter !== 'all' ? (
            <>
              <p>{debouncedSearch ? 'No threads match your search' : 'No threads of this type yet'}</p>
              <button
                onClick={() => { setSearchQuery(''); setTypeFilter('all'); }}
                className="clear-search-button-large"
                type="button"
              >
                {debouncedSearch ? 'Clear search' : 'Show all threads'}
              </button>
            </>
          ) : (
            <div className="inbox-empty-state">
              <img src={AssetUrls.logos.arevakhachIcon} alt="TradeMagic" className="inbox-empty-state-logo" />
              {archived ? (
                <div className="inbox-empty-state-title">Nothing archived yet.</div>
              ) : (
                <>
                  <div className="inbox-empty-state-title">Your inbox is quiet.</div>
                  <div className="inbox-empty-state-message">Start a trade and it won&apos;t be for long.</div>
                  <div className="inbox-empty-state-button">
                    <MagicButton label="Find Cards to Send" color="purple" onPress={() => navigate('/sending')} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <ThreadList
            rows={rows}
            groupCounts={data?.groupCounts}
            selectedIds={selectedIds}
            activeId={activeId}
            showCheckboxes={!isMobile}
            onOpen={openThread}
            onToggleSelect={handleCheckboxClick}
            onContextMenu={handleRowContextMenu}
            onArchive={handleArchive}
          />
          {data?.pagination && data.pagination.total > data.pagination.pageSize && (
            <div className="inbox-pagination">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="pagination-button" type="button">
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.ceil(data.pagination.total / data.pagination.pageSize)}
              </span>
              <button disabled={!data.pagination.hasMore} onClick={() => setPage((p) => p + 1)} className="pagination-button" type="button">
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );

  return (
    <div ref={workspaceRef} className={`inbox-workspace${activeId ? ' has-thread' : ''}`}>
      {listPane}

      {/* Both panes stay at width 0 and empty until a thread is selected. */}
      <section className="inbox-thread-pane" aria-label="Conversation" aria-hidden={!activeId}>
        {renderedId && (
          <ThreadPane
            key={renderedId}
            conversationId={renderedId}
            onBack={isMobile ? () => navigate('/inbox') : undefined}
            belowHeader={
              isMobile ? (
                <MobileContextStrip conversationId={renderedId} onOpenDetails={() => setShowDetailsSheet(true)} />
              ) : undefined
            }
          />
        )}
      </section>

      <aside className="inbox-rail-pane" aria-label="Details" aria-hidden={!activeId}>
        {renderedId && !isMobile && (
          <div className="inbox-rail-scroll">
            <ContextRail conversationId={renderedId} viewerUsername={meData?.username} />
          </div>
        )}
      </aside>

      {isMobile && activeId && showDetailsSheet && (
        <ContextSheet
          conversationId={activeId}
          viewerUsername={meData?.username}
          onClose={() => setShowDetailsSheet(false)}
        />
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenuItems}
          onClose={closeContextMenu}
          subtitle={contextMenuSubtitle}
        />
      )}

      <BubbleColorPickerModal
        isOpen={showBubbleColorModal}
        onClose={() => setShowBubbleColorModal(false)}
        currentColor1={meData?.bubbleColor1 || '#8B5CF6'}
        currentColor2={meData?.bubbleColor2 || '#6D28D9'}
        onSaveOverride={!isElite ? handleEliteRedirect : undefined}
      />
    </div>
  );
}
